// Schema capabilities are not authorization. Every real read still uses its
// unchanged credentials and owner/scope variables and passes server guards.
const { partDetailsSelection } = require('../../../utils/recordingPartDetails');
const { partialOutcomeSelection } = require('../../../utils/recordingPartialOutcome');
const capabilityQuery = `query DashboardListCapabilities {
  room: __type(name: "Room") { fields { name } }
  recording: __type(name: "Recording") { fields { name } }
  partial: __type(name: "RecordingPartialOutcome") { fields { name } }
  omitted: __type(name: "RecordingOmittedPart") { fields { name } }
  missing: __type(name: "RecordingMissingInterval") { fields { name } }
  rooms: __type(name: "SpecificRoomInput") { inputFields { name } }
  recordings: __type(name: "SpecificRecordingInput") { inputFields { name } }
  domains: __type(name: "SpecificAllowedDomainsInput") { inputFields { name } }
}`;
/* One probe per server and session for five minutes, not one per list read.
   Only an answered probe is kept; a failed one is retried next time. */
const CAPABILITY_TTL_MS = 5 * 60 * 1000;
const capabilityCache = new WeakMap();
const readDashboardListCapabilities = (baseUrl, fetchImpl, headers) => {
  if (typeof fetchImpl !== 'function') return probeDashboardListCapabilities(baseUrl, fetchImpl, headers).then(r => r.caps);
  let byServer = capabilityCache.get(fetchImpl);
  if (!byServer) { byServer = new Map(); capabilityCache.set(fetchImpl, byServer); }
  const key = `${baseUrl}\n${headers?.Authorization || headers?.authorization || ''}`;
  const hit = byServer.get(key);
  if (hit && Date.now() - hit.at < CAPABILITY_TTL_MS) return hit.promise;
  const promise = probeDashboardListCapabilities(baseUrl, fetchImpl, headers).then((result) => {
    if (!result.answered) byServer.delete(key);
    return result.caps;
  });
  byServer.set(key, { at: Date.now(), promise });
  return promise;
};
const probeDashboardListCapabilities = async (baseUrl, fetchImpl, headers) => {
  try {
    const res = await fetchImpl(baseUrl, { method: 'POST', headers,
      body: JSON.stringify({ query: capabilityQuery }), signal: AbortSignal.timeout(5000) });
    const body = await res.json();
    if (!res.ok || body.errors?.length || !body.data) return { answered: false, caps: { diagnostics: false, partialOutcome: false, globalScope: false } };
    const has = (name, kind, field) => Array.isArray(body.data[name]?.[kind]) &&
      body.data[name][kind].some(row => row?.name === field);
    return { answered: true, caps: { diagnostics: has('room', 'fields', 'recordingPartDiagnostics'),
      partialOutcome: has('recording', 'fields', 'partialOutcome') &&
        ['schemaVersion', 'outcome', 'originalDurationMs', 'playbackDurationMs', 'expectedPartCount', 'includedLogicalParts',
          'omittedLogicalParts', 'missingIntervals', 'completeCoverage', 'normalCompletionAllowed'].every(field => has('partial', 'fields', field)) &&
        ['index', 'reason', 'permanentSourceLossClaimed'].every(field => has('omitted', 'fields', field)) &&
        ['kind', 'from', 'to', 'durationMs', 'clockCandidateOnly'].every(field => has('missing', 'fields', field)),
      globalScope: ['rooms', 'recordings', 'domains'].every(name => has(name, 'inputFields', 'globalScope')) } };
  } catch (_) { return { answered: false, caps: { diagnostics: false, partialOutcome: false, globalScope: false } }; }
};
const recordingRequestForCapabilities = (request, capabilities) => {
  if (typeof request?.query !== 'string' || !/^\s*query RecordingsSpecific\b/.test(request.query)) return request;
  let query = request.query;
  if (capabilities?.diagnostics !== true) query = query.replace(partDetailsSelection, '');
  if (capabilities?.partialOutcome !== true) query = query.replace(partialOutcomeSelection, '');
  return query === request.query ? request : Object.assign({}, request, { query });
};
module.exports = { capabilityQuery, readDashboardListCapabilities, recordingRequestForCapabilities };
